// AN-2 — the animation ⇄ bodymap pair inside the `?` popup.
//
// His words, and the shape comes straight from them: «sotto la descrizione ci
// sono l'animazione e, scrollabile, la bodymap — con solo bodymap quando
// l'animazione non esiste». So only this band moves: the description above and
// the alternatives below never enter the pager.
//
// ⭐ "Scrollabile" dictates the implementation. This is not two boxes swapped
// with display:none — it is a horizontal ScrollView with pagingEnabled, which
// gives the native swipe for free.
//
// ⚠️ The plan warned that ScrollView is where the two platforms diverge most,
// so `pagingEnabled` was checked instead of assumed — and the warning does not
// apply: react-native-web 0.21.2 implements it, as CSS `scroll-snap-type: x
// mandatory` on the container plus `scroll-snap-align: start` on each child
// (dist/exports/ScrollView/index.js:641-649). The first version of this file
// also passed `snapToInterval` to shore the web up; that prop is not in
// r-n-w's forwarded set at all, so it was dead there, redundant on native, and
// its comment said the opposite of what the source does. Removed.
//
// The arrows stay anyway — his reason, not a platform one: an affordance for
// anyone who does not know the band scrolls.
//
// ⭐⭐ And the model is a LIST of pages that happens to be 1 or 2 long, not two
// pages with a special case. With an animation it is [animation, map]; without,
// it is [map]. Arrows and dots appear only when there is more than one page, so
// «no animation» is not a branch anyone programs — it is the list of length
// one, and 162 of 243 exercises are in it. ⛔ No placeholder, no "missing"
// badge, no empty space: without a GIF this renders what it rendered before.
//
// ⛔ This component does not know where the GIFs live. It measures how wide it
// actually is, asks the host for a URL at that pixel width, and takes `null`
// for an answer. Local file, S3 or CDN all look the same from here — which is
// what keeps AN-3 out of this file.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Image,
  Modal,
  PixelRatio,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { useReducedMotion } from '../useReducedMotion';
import { useTheme, type Theme } from './theme';
import { IconButton } from '../primitives/IconButton';
import { PagerNav } from '../primitives/PagerNav';

/**
 * How the host turns an exercise into an animation URL.
 *
 * ⛔ Returns `null` for "there is no animation", which is the NORMAL answer,
 * not an error: most of the catalog has none, and during AN-3's background
 * sync a not-yet-cached GIF answers `null` too. Both are the same case here,
 * which is exactly why the seam is shaped this way.
 *
 * `widthPx` is device pixels, already measured — the GIFs are square and the
 * number in their name is the side, so the host can pick the smallest file
 * that covers the box instead of shipping 1.6 MB for a thumbnail.
 */
export type AnimationSourceFn = (
  slug: string,
  widthPx: number,
  /** ⛔ `still` = il primo fotogramma, per chi ha chiesto di ridurre il
   *  movimento. Chi implementa il seam PUO' ignorarlo e rispondere sempre col
   *  file animato: l'assenza del fermo-immagine e' uno stato legittimo, e in
   *  quel caso il pager preferisce nascondere l'animazione piuttosto che farla
   *  girare contro la preferenza di sistema. */
  variant?: "motion" | "still",
) => string | null;

export type ExerciseVisualPagerProps = {
  /** Catalog slug of the exercise on show. */
  slug: string;
  /** Localised exercise name, for the accessible labels. */
  exerciseName: string;
  /** Host resolver. Absent = no animations anywhere = today's surface. */
  animationSource?: AnimationSourceFn;
  /** The bodymap block exactly as the caller already renders it. */
  children: React.ReactNode;
};


export function ExerciseVisualPager({
  slug,
  exerciseName,
  animationSource,
  children,
}: ExerciseVisualPagerProps) {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const { width: screenWidth } = useWindowDimensions();

  const scrollRef = useRef<ScrollView>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [page, setPage] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setBoxWidth(Math.round(e.nativeEvent.layout.width));
  }, []);

  // ⛔ The pages have no width until the measurement above lands, so the
  // scroller's content jumps from ~0 to 2×width in one commit — and the
  // browser's scroll-snap engine then re-snaps to whichever child is nearest,
  // which is page TWO. Measured headlessly: scrollLeft 300 of a 600 scrollWidth,
  // i.e. the popup opened on the BODYMAP while the counter read "1 of 2". His
  // rule is that the animation comes first, and it was the second thing you saw.
  // ⚠️ Nothing in the unit suite could have caught it: those assert what is
  // RENDERED, and both pages are always rendered. It took a real browser.
  // An effect, not the layout callback: this has to run after the commit that
  // gives the pages their width, or it scrolls the old geometry.
  useEffect(() => {
    if (boxWidth <= 0) return;
    setPage(0);
    scrollRef.current?.scrollTo({ x: 0, animated: false });
  }, [boxWidth]);

  // ⚠️ Measured, never assumed — the width AND the density. The doc's "720 is
  // the right cut" was arithmetic on a 360pt phone at density 3; a tablet, or
  // the web at any other zoom, needs a different file.
  // ⛔ This was a hardcoded `DENSITY = 3` first, in the function whose comment
  // says not to assume. A headless render at deviceScaleFactor 2 asked for
  // 900px where the truth was 600 — a whole size band wrong on every browser
  // that is not 3×. PixelRatio.get() is real on both platforms (r-n-w maps it
  // to Dimensions' scale, i.e. devicePixelRatio).
  // ⛔ Chi ha chiesto al sistema di RIDURRE IL MOVIMENTO non deve vedere un
  // loop, ed e' esattamente cio' che una GIF fa: `<Image>` non sa metterla in
  // pausa. L'unica risposta e' chiedere un file che non si muove — il primo
  // fotogramma. ⭐ Il contenuto resta (si vede la posizione dell'esercizio), a
  // sparire e' il moto. Prima di AN-2b la GIF girava lo stesso, e le altre due
  // righe di questo file che leggono `reducedMotion` (lo scroll e la
  // transizione della modale) lo rendevano peggio: rispettavamo la preferenza
  // su cio' che dura 200 ms e la ignoravamo su cio' che gira per sempre.
  const variant = reducedMotion ? 'still' : 'motion';

  const inlineUrl = useMemo(() => {
    if (!animationSource || boxWidth <= 0) return null;
    return animationSource(slug, Math.round(boxWidth * PixelRatio.get()), variant);
  }, [animationSource, slug, boxWidth, variant]);

  // Only fetched once the zoom is actually open — the whole point of the tap.
  const zoomUrl = useMemo(() => {
    if (!animationSource || !zoomed) return null;
    return animationSource(slug, Math.round(screenWidth * PixelRatio.get()), variant);
  }, [animationSource, slug, zoomed, screenWidth, variant]);

  const hasAnimation = inlineUrl !== null;
  const pageCount = hasAnimation ? 2 : 1;

  const onMomentumEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (boxWidth <= 0) return;
      setPage(Math.round(e.nativeEvent.contentOffset.x / boxWidth));
    },
    [boxWidth],
  );

  const goTo = useCallback(
    (next: number) => {
      setPage(next);
      // accessibilityLiveRegion is Android + web only (ViewAccessibility.d.ts
      // says "API >= 19 only"; r-n-w maps it to aria-live). On iOS the counter
      // would change with nothing spoken, and the counter is the ONLY page
      // feedback there is — so announce it explicitly too.
      AccessibilityInfo.announceForAccessibility(
        t('workout.pageOf', {
          defaultValue: '{{current}} of {{total}}',
          current: next + 1,
          total: pageCount,
        }),
      );
      scrollRef.current?.scrollTo({
        x: next * boxWidth,
        animated: !reducedMotion,
      });
    },
    [boxWidth, reducedMotion, pageCount, t],
  );

  // One page means no chrome at all: no arrows, no dots, nothing to announce.
  // The athlete sees precisely what they saw before AN-2 existed.
  const showChrome = pageCount > 1;

  return (
    <View onLayout={onLayout} style={styles.wrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onMomentumEnd}
        scrollEnabled={showChrome}
        accessibilityRole="none"
      >
        {hasAnimation && inlineUrl ? (
          <Pressable
            onPress={() => setZoomed(true)}
            style={[styles.page, { width: boxWidth || undefined }]}
            accessibilityRole="button"
            accessibilityLabel={t('workout.animationOf', {
              defaultValue: 'Animation of {{name}}',
              name: exerciseName,
            })}
            accessibilityHint={t('workout.animationZoomHint', {
              defaultValue: 'Opens the animation full screen',
            })}
          >
            <Image
              source={{ uri: inlineUrl }}
              style={styles.animation}
              resizeMode="contain"
              accessible={false}
            />
          </Pressable>
        ) : null}

        {/* ⛔ Both pages stay MOUNTED — that is what makes the swipe work — so
            the off-screen one has to be taken out of the accessibility tree by
            hand. Without this a screen reader walks straight from the
            animation into a bodymap nobody can see. */}
        <View
          style={[styles.page, { width: boxWidth || undefined }]}
          accessibilityElementsHidden={hasAnimation && page !== 1}
          importantForAccessibility={
            hasAnimation && page !== 1 ? 'no-hide-descendants' : 'auto'
          }
        >
          {children}
        </View>
      </ScrollView>

      {/* ⛔ AN-2b: la chrome era scritta a mano qui dentro (un `PagerArrow`
          locale piu' un contatore), e `PagerNav` faceva gia' la stessa cosa —
          frecce da 44pt, pallini che raggiungono il pavimento con padding e
          non con un box piu' grande, contatore mono cosi' la posizione non e'
          mai solo-colore. Due chrome di pager per un concetto solo, e la
          seconda l'avevo scritta io. ⭐ Rende `null` da se' per una pagina
          sola, quindi `showChrome` non serve piu' come guardia. */}
      <PagerNav
        theme={theme}
        index={page}
        count={pageCount}
        onGoTo={goTo}
        prevLabel={t('workout.showAnimation', {
          defaultValue: 'Show the animation',
        })}
        nextLabel={t('workout.showBodyMap', {
          defaultValue: 'Show the muscles worked',
        })}
        positionLabel={t('workout.pageOf', {
          defaultValue: '{{current}} of {{total}}',
          current: page + 1,
          total: pageCount,
        })}
        // ⛔ NON la stessa stringa delle frecce: il pallino e la freccia
        // portano allo stesso posto, ma uno screen reader che sente due volte
        // «Show the animation» non sa che sono due controlli diversi. Il
        // pallino dice dove salta E dove sta, la forma che il primitivo
        // documenta («Record 2 of 5»).
        // ⛔ UNA chiave sola con dei segnaposto, non due frasi incollate: la
        // frase montata a mano fissa l'ordine delle parole dell'inglese in
        // tutte e cinque le lingue, ed e' il traduttore a doverlo decidere.
        // (E l'em dash che c'era qui prima non sta in una stringa spedita.)
        dotLabel={(i) =>
          t('workout.pagerDot', {
            defaultValue: '{{target}}, {{current}} of {{total}}',
            target:
              i === 0
                ? t('workout.showAnimation', {
                    defaultValue: 'Show the animation',
                  })
                : t('workout.showBodyMap', {
                    defaultValue: 'Show the muscles worked',
                  }),
            current: i + 1,
            total: pageCount,
          })
        }
      />

      <Modal
        visible={zoomed}
        transparent={false}
        // Android's system back closes it — not just the ✕.
        onRequestClose={() => setZoomed(false)}
        animationType={reducedMotion ? 'none' : 'fade'}
      >
        {/* ⛔ Lo sfondo CHIUDE al tap e non ha piu' un nome, ed e' un cambio
            voluto di AN-2b: da quando esiste la ✕ visibile, lasciarglielo
            metteva DUE controlli con la stessa etichetta sulla stessa
            superficie — e il primo dei due e' un bersaglio grande quanto lo
            schermo che si mangia l'immagine. Il gesto resta per chi tocca;
            il nome, uno solo, ce l'ha la ✕. */}
        <Pressable
          style={styles.zoomBackdrop}
          onPress={() => setZoomed(false)}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
        >
          {/* Same rule as the inline one: the Pressable around it owns the
              accessible name, so this must not be a second stop that says
              "image" and never mentions that a tap closes the surface. */}
          {zoomUrl ? (
            <Image
              source={{ uri: zoomUrl }}
              style={styles.zoomImage}
              resizeMode="contain"
              accessible={false}
            />
          ) : null}
        </Pressable>

        {/* ⛔ AN-2b — la ✕ VISIBILE, sua richiesta. Prima si chiudeva col tap
            sull'immagine e col back di Android: due gesti che funzionano e che
            nessuno annuncia. Un pieno schermo nero senza un'uscita visibile e'
            il posto dove ci si sente in trappola, e il back di sistema non
            esiste sul web.
            ⭐ E' un `IconButton`, non una Pressable a mano: quel primitivo
            porta gia' il pavimento dei 44pt sul web (`minWidth/minHeight`),
            che e' esattamente il difetto per cui la ✕ del «?» ha una voce
            aperta (`UI-TIPCLOSE`) — la` hitSlop` li' non esiste in r-n-w.
            ⚠️ Sta FUORI dalla Pressable di sfondo e dopo di lei nell'albero:
            dentro, il tap la chiuderebbe due volte, e prima resterebbe sotto. */}
        <SafeAreaView style={styles.zoomCloseBar} pointerEvents="box-none">
          {/* ⛔ `primary`, non `cancel`, e la ragione e' MISURATA — la variante
              `cancel` la avevo scelta per tono e sarebbe stata invisibile.
              La lightbox e' nera in entrambi i temi, mentre `cancel` si veste
              coi token dell'app: sul tema scuro il suo fondo (#1d1f21) contro
              il nero fa **1,27:1**, sotto il pavimento di 3:1 che WCAG 1.4.11
              chiede al bordo di un controllo. Il glifo si sarebbe visto e il
              bottone no, cioe' l'opposto di una ✕ VISIBILE.
              `primary` (brand600) fa **5,42:1** sul tema chiaro e **8,02:1**
              sullo scuro, e su una lightbox la chiusura E' l'azione primaria.
              ⚠️ Chi cambia questa variante rifa' il conto: il fondo qui non
              e' una superficie dell'app. */}
          <IconButton
            theme={theme}
            name="close"
            variant="primary"
            onPress={() => setZoomed(false)}
            accessibilityLabel={t('workout.closeAnimation', {
              defaultValue: 'Close the animation',
            })}
          />
        </SafeAreaView>
      </Modal>
    </View>
  );
}

const makeStyles = (theme: Theme) =>
  StyleSheet.create({
    wrap: { width: '100%' },
    page: { alignItems: 'center', justifyContent: 'center' },
    // Square, because the GIFs are square and the number in the filename is
    // the side. Full width is his decision from the mockup (ipotesi B).
    animation: { width: '100%', aspectRatio: 1 },
    // ⛔ Nero, non un token, e non e' una svista: dietro un'immagine a pieno
    // schermo il nero e' corretto in entrambi i temi, ed e' la stessa scelta
    // che fanno gia' `SupersetMenuSheet` e `EntryTipModal` in questa cartella.
    // Un token inventato per un caso solo sarebbe un token che mente.
    zoomBackdrop: {
      flex: 1,
      backgroundColor: '#000',
      alignItems: 'center',
      justifyContent: 'center',
    },
    zoomImage: { width: '100%', height: '100%' },
    // Sopra l'immagine e in alto a destra. `SafeAreaView` perche' su un
    // telefono col notch il 16 nudo finisce sotto la status bar: e' il
    // fratello che ExerciseSwapPicker usa gia' in questa cartella.
    zoomCloseBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      alignItems: 'flex-end',
      padding: theme.spacing.s5,
    },
  });
